import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

// Only clear the local session when the server explicitly rejects the token
// with a 401. Every other failure (network flap, 5xx during a deploy, offline
// tab, etc.) must keep the session alive — signing out is user-driven only.
function isTokenInvalidError(err) {
  return err?.response?.status === 401;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=none, obj=user
  useEffect(() => {
    // Skip /me during Google OAuth callback — AuthCallback will run first.
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    const token = localStorage.getItem("fl_token");
    if (!token) {
      // No local token — try the httpOnly cookie flow (Emergent-Google session).
      // Never clear anything on failure here; there was nothing to clear.
      api.get("/auth/me", { withCredentials: true })
        .then((r) => setUser(r.data))
        .catch(() => setUser(false));
      return;
    }
    api.get("/auth/me", { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch((err) => {
        if (isTokenInvalidError(err)) {
          // Server says the token is dead — force a login.
          localStorage.removeItem("fl_token");
          setUser(false);
          return;
        }
        // Transient failure (network / 5xx / preflight). Preserve the token
        // and optimistically restore the session from the JWT payload so the
        // UI doesn't bounce to /login. The next request that actually needs
        // fresh /me data will retry naturally.
        try {
          const payload = JSON.parse(atob(token.split(".")[1] || ""));
          if (payload?.sub) {
            setUser({ id: payload.sub, email: payload.email, role: payload.role });
            return;
          }
        } catch { /* fall through */ }
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) {
      // Caller (Login page) handles the two-step flow; don't set user yet.
      return { mfa_required: true, mfa_token: data.mfa_token };
    }
    localStorage.setItem("fl_token", data.token);
    setUser(data);
    return data;
  };
  const loginMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/login/mfa", { mfa_token, code });
    localStorage.setItem("fl_token", data.token);
    setUser(data);
    return data;
  };
  const loginMfaRecovery = async (mfa_token, code) => {
    // Fallback path when the user has lost access to their authenticator
    // app. Consumes one single-use recovery code and completes the login.
    const { data } = await api.post("/auth/login/mfa/recovery", { mfa_token, code });
    localStorage.setItem("fl_token", data.token);
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("fl_token", data.token);
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("fl_token");
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, login, loginMfa, loginMfaRecovery, register, logout }}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);

export function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
